/**
 * QuickQuestionPrompt — the in-app quick-question prompt (T35.4 / F21 B).
 *
 * An occasional PM-authored question, surfaced AMBIENTLY in the Feedback tab's
 * reserved slot. It is calm by construction: a single dismissible card, on-screen
 * ONLY — never a notification, no sound — that the user can answer in one tap or
 * dismiss with no penalty. The server decides eligibility (targeting + the
 * frequency cap); this component only renders whatever the one `GET` returns, and
 * renders NOTHING when there is no question.
 *
 * The three answer shapes mirror the prototype `#pq` block: `choice` (tap chips),
 * `reaction` (emoji buttons), `sentence` (a short free-text input → stored as
 * `answertext` on the response row). Answering or dismissing resolves the card in
 * place to a quiet end state.
 *
 * Mode-independent (T39): Feedback does not consume App mode or location
 * context. The prompt renders by its own rules only — an eligible question from
 * the server (and the loading / gone / answered / dismissed phases) — in any App
 * mode. Its primary placement is the Feedback tab, separate from the list.
 *
 * Tokens-only (CP5); mobile-first; ≥44px targets; visible keyboard focus via the
 * shared `:focus-visible` pattern (the chips/buttons are native, so the global
 * focus ring applies).
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Icon } from '../../components';
import {
  getEligibleQuickQuestion,
  answerQuickQuestion,
  dismissQuickQuestion,
  type EligibleQuickQuestion,
} from './api';

// The post-action phase. `prompt` = the live question; `answered` / `dismissed` =
// the calm in-place end states; `gone` = nothing to show (no question / failed).
type Phase = 'loading' | 'prompt' | 'answered' | 'dismissed' | 'gone';

// The four reaction emoji + their accessible labels (mirrors the proto reaction row).
const REACTIONS: ReadonlyArray<readonly [emoji: string, label: string]> = [
  ['😍', 'Love it'],
  ['🙂', 'Like it'],
  ['😐', 'Neutral'],
  ['🙁', 'Dislike'],
];

const cardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
  padding: 'var(--space-4)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-bg-raised)',
};

const eyebrowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-primary)',
};

const dismissBtnStyle: CSSProperties = {
  marginLeft: 'auto',
  width: '2.75rem',
  height: '2.75rem',
  border: 0,
  background: 'transparent',
  padding: 0,
  borderRadius: 'var(--radius-pill)',
  display: 'grid',
  placeItems: 'center',
  color: 'var(--color-text-muted)',
  cursor: 'pointer',
};

const questionStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-base)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.4,
};

const choicesStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
};

const choiceBtnStyle: CSSProperties = {
  minHeight: '2.75rem',
  border: '0.0625rem solid var(--color-border-strong)',
  background: 'var(--color-bg-primary)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-2) var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const reactRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
};

const reactBtnStyle: CSSProperties = {
  fontSize: 'var(--font-size-xl)',
  lineHeight: 1,
  background: 'var(--color-bg-primary)',
  border: '0.0625rem solid var(--color-border-subtle)',
  borderRadius: 'var(--radius-md)',
  width: '2.875rem',
  height: '2.75rem',
  cursor: 'pointer',
};

const sentenceRowStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
};

const sentenceInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: '0.0625rem solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  fontFamily: 'var(--font-family-sans)',
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

const sendBtnStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const endStateStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
};

const checkStyle: CSSProperties = {
  flex: '0 0 auto',
  color: 'var(--color-status-success)',
  marginTop: '0.0625rem',
};

export function QuickQuestionPrompt(): JSX.Element | null {
  const [phase, setPhase] = useState<Phase>('loading');
  const [question, setQuestion] = useState<EligibleQuickQuestion | null>(null);
  const [sentence, setSentence] = useState('');
  const [busy, setBusy] = useState(false);

  // Load the one eligible question. A failed / empty read leaves the prompt gone —
  // it never blocks the Feedback tab, and the cap means "no question" is the
  // common case. Loaded once on mount.
  useEffect(() => {
    let cancelled = false;
    getEligibleQuickQuestion()
      .then((q) => {
        if (cancelled) return;
        if (q) {
          setQuestion(q);
          setPhase('prompt');
        } else {
          setPhase('gone');
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('gone');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // T39 — no App-mode / location guard. Render purely by phase + question.
  if (phase === 'loading' || phase === 'gone' || !question) return null;

  async function submitAnswer(payload: {
    answerValue?: unknown;
    freeText?: string | null;
  }): Promise<void> {
    if (!question || busy) return;
    setBusy(true);
    try {
      await answerQuickQuestion(question.id, payload);
      setPhase('answered');
    } catch {
      // A failed write leaves the prompt up so the user can retry; no nag.
      setBusy(false);
    }
  }

  async function onDismiss(): Promise<void> {
    if (!question || busy) return;
    setBusy(true);
    // Resolve to the dismissed end state immediately — dismissal has no penalty and
    // must feel instant. The server records it best-effort; a failed write still
    // leaves the user with a calm dismissed card (the cap re-evaluates next load).
    setPhase('dismissed');
    try {
      await dismissQuickQuestion(question.id);
    } catch {
      // best-effort
    }
  }

  if (phase === 'answered') {
    return (
      <div style={cardStyle} data-testid="quick-question" data-state="answered">
        <p style={endStateStyle} data-testid="quick-question-answered">
          <span style={checkStyle} aria-hidden="true">
            <Icon name="check" />
          </span>
          <span>Thanks — noted. This is exactly the signal that decides what we build next.</span>
        </p>
      </div>
    );
  }

  if (phase === 'dismissed') {
    return (
      <div style={cardStyle} data-testid="quick-question" data-state="dismissed">
        <p style={endStateStyle} data-testid="quick-question-dismissed">
          <span>No problem — we won&apos;t ask this one again.</span>
        </p>
      </div>
    );
  }

  // phase === 'prompt' with a question.
  return (
    <div style={cardStyle} data-testid="quick-question" data-state="prompt">
      <div style={eyebrowStyle}>
        <Icon name="chat" />
        <span>Quick question</span>
        <button
          type="button"
          style={dismissBtnStyle}
          onClick={onDismiss}
          disabled={busy}
          title="Not now — no penalty"
          aria-label="Dismiss this question"
          data-testid="quick-question-dismiss"
        >
          <Icon name="close" />
        </button>
      </div>

      <p style={questionStyle} data-testid="quick-question-text">
        {question.text}
      </p>

      {question.answerType === 'choice' && (
        <div style={choicesStyle} data-testid="quick-question-choices">
          {(question.choices ?? []).map((choice) => (
            <button
              key={choice}
              type="button"
              style={choiceBtnStyle}
              disabled={busy}
              onClick={() => submitAnswer({ answerValue: { value: choice } })}
              data-testid="quick-question-choice"
            >
              {choice}
            </button>
          ))}
        </div>
      )}

      {question.answerType === 'reaction' && (
        <div style={reactRowStyle} data-testid="quick-question-reactions">
          {REACTIONS.map(([emoji, label]) => (
            <button
              key={label}
              type="button"
              style={reactBtnStyle}
              disabled={busy}
              aria-label={label}
              onClick={() => submitAnswer({ answerValue: { value: label } })}
              data-testid="quick-question-reaction"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {question.answerType === 'sentence' && (
        <form
          style={sentenceRowStyle}
          data-testid="quick-question-sentence"
          onSubmit={(e) => {
            e.preventDefault();
            void submitAnswer({ freeText: sentence });
          }}
        >
          <input
            style={sentenceInputStyle}
            value={sentence}
            onChange={(e) => setSentence(e.target.value)}
            aria-label="Your answer"
            placeholder="A sentence is plenty (optional)…"
            data-testid="quick-question-input"
          />
          <button type="submit" style={sendBtnStyle} disabled={busy} data-testid="quick-question-send">
            Send
          </button>
        </form>
      )}
    </div>
  );
}
